import {
  LEAGUE_ID_BRONZE,
  LEAGUE_ID_DIAMOND,
  LEAGUE_ID_GOLD,
  LEAGUE_ID_MASTER,
  LEAGUE_ID_PLATINUM,
  LEAGUE_ID_SILVER,
  LEAGUE_LIMITS,
  LEAGUE_NAME_BRONZE,
  LEAGUE_NAME_DIAMOND,
  LEAGUE_NAME_GOLD,
  LEAGUE_NAME_MASTER,
  LEAGUE_NAME_PLATINUM,
  LEAGUE_NAME_SILVER,
} from "@chesshotel/constants";

export type PuzzleLeagueName =
  | typeof LEAGUE_NAME_BRONZE
  | typeof LEAGUE_NAME_SILVER
  | typeof LEAGUE_NAME_GOLD
  | typeof LEAGUE_NAME_PLATINUM
  | typeof LEAGUE_NAME_DIAMOND
  | typeof LEAGUE_NAME_MASTER;

export type PuzzleLeagueTier = {
  label: string;
  leagueName: PuzzleLeagueName;
  leagueId: number;
  rank: number;
  minRating: number;
};

export type PuzzleSolvedProgress = {
  solved: number;
  completedPuzzleIds: string[];
};

export type PuzzleLeagueProgressView = {
  currentTier: PuzzleLeagueTier;
  nextTier: PuzzleLeagueTier | null;
  rating: number;
  previousRating: number;
  nextRating: number | null;
  percent: number;
};

export type PuzzleRankProgressView = {
  nextRank: number | null;
  nextRankPoints: number | null;
  percent: number;
  title: string;
  progressText: string;
};

const MAX_TRACKED_COMPLETED_PUZZLES = 500;
export const PUZZLE_PLACEMENT_TARGET = 10;
export const PUZZLE_RATING_FLOOR = 800;
export const PUZZLE_RANK_TIERS = [100, 50, 25, 8];

export const PUZZLE_LEAGUE_TIERS: PuzzleLeagueTier[] = [
  {
    label: "Bronze",
    leagueName: LEAGUE_NAME_BRONZE,
    leagueId: LEAGUE_ID_BRONZE,
    rank: 8,
    minRating: PUZZLE_RATING_FLOOR,
  },
  {
    label: "Silver",
    leagueName: LEAGUE_NAME_SILVER,
    leagueId: LEAGUE_ID_SILVER,
    rank: 8,
    minRating: LEAGUE_LIMITS[LEAGUE_ID_SILVER],
  },
  {
    label: "Gold",
    leagueName: LEAGUE_NAME_GOLD,
    leagueId: LEAGUE_ID_GOLD,
    rank: 8,
    minRating: LEAGUE_LIMITS[LEAGUE_ID_GOLD],
  },
  {
    label: "Platinum",
    leagueName: LEAGUE_NAME_PLATINUM,
    leagueId: LEAGUE_ID_PLATINUM,
    rank: 8,
    minRating: LEAGUE_LIMITS[LEAGUE_ID_PLATINUM],
  },
  {
    label: "Diamond",
    leagueName: LEAGUE_NAME_DIAMOND,
    leagueId: LEAGUE_ID_DIAMOND,
    rank: 8,
    minRating: LEAGUE_LIMITS[LEAGUE_ID_DIAMOND],
  },
  {
    label: "Master",
    leagueName: LEAGUE_NAME_MASTER,
    leagueId: LEAGUE_ID_MASTER,
    rank: 8,
    minRating: LEAGUE_LIMITS[LEAGUE_ID_MASTER],
  },
];

export function createEmptyPuzzleSolvedProgress(): PuzzleSolvedProgress {
  return {
    solved: 0,
    completedPuzzleIds: [],
  };
}

export function getPuzzleSolvedProgressStorageKey(username?: string) {
  return username
    ? `puzzle_solved_progress_${username}`
    : "puzzle_solved_progress_guest";
}

export function getPuzzleLeagueMedalImageSrc(tier: PuzzleLeagueTier) {
  if (tier.leagueName === LEAGUE_NAME_MASTER) {
    return `/images/medals/master${tier.rank}_v2_square.svg`;
  }

  return `/images/medals/${tier.leagueName}${tier.rank}_square.svg`;
}

export function getPuzzleLeagueRankMedalImageSrc(
  tier: PuzzleLeagueTier,
  rank: number,
) {
  return getPuzzleLeagueMedalImageSrc({
    ...tier,
    rank,
  });
}

export function getPuzzleLeagueTierForName(leagueName?: string | null) {
  return (
    PUZZLE_LEAGUE_TIERS.find((tier) => tier.leagueName === leagueName) || null
  );
}

export function getPuzzleLeagueTierForRating(rating: number) {
  let tier = PUZZLE_LEAGUE_TIERS[0];

  PUZZLE_LEAGUE_TIERS.forEach((leagueTier) => {
    if (rating >= leagueTier.minRating) {
      tier = leagueTier;
    }
  });

  return tier;
}

export function getPuzzleLeagueTier({
  rating,
  leagueName,
}: {
  rating: number;
  leagueName?: string | null;
}) {
  return (
    getPuzzleLeagueTierForName(leagueName) ||
    getPuzzleLeagueTierForRating(rating)
  );
}

function normalizePuzzleSolvedProgress(value: unknown): PuzzleSolvedProgress {
  if (!value || typeof value !== "object") {
    return createEmptyPuzzleSolvedProgress();
  }

  const progress = value as Partial<PuzzleSolvedProgress>;
  const solved = Number.isFinite(progress.solved) ? Number(progress.solved) : 0;
  const completedPuzzleIds = Array.isArray(progress.completedPuzzleIds)
    ? progress.completedPuzzleIds.filter(
        (puzzleId): puzzleId is string => typeof puzzleId === "string",
      )
    : [];

  return {
    solved,
    completedPuzzleIds: completedPuzzleIds.slice(-MAX_TRACKED_COMPLETED_PUZZLES),
  };
}

export function readPuzzleSolvedProgress(storageKey: string) {
  if (typeof window === "undefined") {
    return createEmptyPuzzleSolvedProgress();
  }

  try {
    const storedProgress = window.localStorage.getItem(storageKey);
    return normalizePuzzleSolvedProgress(
      storedProgress ? JSON.parse(storedProgress) : null,
    );
  } catch (e) {
    console.error("Error reading puzzle solved progress", e);
    return createEmptyPuzzleSolvedProgress();
  }
}

function savePuzzleSolvedProgress(
  storageKey: string,
  progress: PuzzleSolvedProgress,
) {
  if (typeof window === "undefined") return;

  try {
    window.localStorage.setItem(storageKey, JSON.stringify(progress));
  } catch (e) {
    console.error("Error saving puzzle solved progress", e);
  }
}

export function recordPuzzleSolved(storageKey: string, puzzleId: string) {
  const progress = readPuzzleSolvedProgress(storageKey);

  if (progress.completedPuzzleIds.includes(puzzleId)) {
    return progress;
  }

  const nextProgress = {
    solved: progress.solved + 1,
    completedPuzzleIds: [...progress.completedPuzzleIds, puzzleId].slice(
      -MAX_TRACKED_COMPLETED_PUZZLES,
    ),
  };

  savePuzzleSolvedProgress(storageKey, nextProgress);
  return nextProgress;
}

export function getPuzzlePlacementProgressView(solved: number) {
  const solvedCount = Math.max(0, Math.min(PUZZLE_PLACEMENT_TARGET, solved));

  return {
    solved: solvedCount,
    target: PUZZLE_PLACEMENT_TARGET,
    percent: (solvedCount / PUZZLE_PLACEMENT_TARGET) * 100,
  };
}

export function getPuzzleLeagueProgressView({
  rating,
  leagueName,
}: {
  rating: number;
  leagueName?: string | null;
}): PuzzleLeagueProgressView {
  const currentTier = getPuzzleLeagueTier({ rating, leagueName });
  const currentIndex = PUZZLE_LEAGUE_TIERS.findIndex(
    (tier) => tier.leagueName === currentTier.leagueName,
  );
  const nextTier = PUZZLE_LEAGUE_TIERS[currentIndex + 1] || null;
  const previousRating = currentTier.minRating;
  const nextRating = nextTier?.minRating || null;

  if (!nextTier || !nextRating) {
    return {
      currentTier,
      nextTier: null,
      rating,
      previousRating,
      nextRating: null,
      percent: 100,
    };
  }

  const ratingInTier = Math.max(0, rating - previousRating);
  const ratingTarget = Math.max(1, nextRating - previousRating);

  return {
    currentTier,
    nextTier,
    rating,
    previousRating,
    nextRating,
    percent: Math.min(99, (ratingInTier / ratingTarget) * 100),
  };
}

function getGuestNextRank(points: number) {
  if (points < 100) return { nextRank: 100, nextRankPoints: 100 };
  if (points < 250) return { nextRank: 50, nextRankPoints: 250 };
  if (points < 500) return { nextRank: 25, nextRankPoints: 500 };
  if (points < 1000) return { nextRank: 8, nextRankPoints: 1000 };

  return { nextRank: null, nextRankPoints: null };
}

export function getPuzzleRankProgressView({
  points,
  nextRank,
  nextRankPoints,
}: {
  points: number;
  nextRank?: number | null;
  nextRankPoints?: number | null;
}): PuzzleRankProgressView {
  const fallbackProgress = getGuestNextRank(points);
  const targetRank =
    nextRank === undefined ? fallbackProgress.nextRank : nextRank;
  const targetPoints =
    nextRankPoints === undefined
      ? fallbackProgress.nextRankPoints
      : nextRankPoints;

  if (!targetRank || !targetPoints) {
    return {
      nextRank: null,
      nextRankPoints: null,
      percent: 100,
      title: "Top rank reached",
      progressText: `${points} points`,
    };
  }

  return {
    nextRank: targetRank,
    nextRankPoints: targetPoints,
    percent: Math.min(99, (points / Math.max(1, targetPoints)) * 100),
    title: `Top ${targetRank}`,
    progressText: `${points} / ${targetPoints} points`,
  };
}
